import React from "react";
import { HiOutlineSearch, HiChevronDown } from "react-icons/hi";
import { useMediaQuery } from "../../hooks/useMediaQuery";

// Centered search pill in Marin's design vocabulary (DoD 22): a scope segment
// on the left, a hairline divider, then the search icon + input. Rebuilt in the
// app idiom (platform materials) and fluid — the pill grows/shrinks with the
// row via the flex:1 container around it, with a max so it stays centered and
// never spans the whole bar on wide screens.
const SearchBar = ({
  searchQuery,
  setSearchQuery,
  searchType,
  setSearchType,
  searchTypes,
  isSearchOpen,
  setIsSearchOpen,
  handleSearch,
}) => {
  const scopeLabel = searchTypes.find((t) => t.value === searchType)?.label;
  // Between md and lg the pill only gets ~230px of the header row, so the long
  // placeholder does not fit next to the scope label and would be cut mid-word.
  // Below lg it gives way to the short form the mobile overlay already uses.
  const isCompact = useMediaQuery("(max-width: 1023px)");
  return (
    <div className="relative w-full max-w-[480px]">
      <form onSubmit={handleSearch} className="relative w-full">
        <div className="flex items-center h-[38px] w-full rounded-xl bg-[#F3F6FB] border border-transparent hover:border-gray-200 box-border">
          {/* Scope segment (left) */}
          <button
            type="button"
            title={scopeLabel}
            aria-label={`Search scope: ${scopeLabel}`}
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            className="flex items-center gap-1.5 h-[38px] pl-3.5 pr-2.5 rounded-l-xl text-[13px] font-semibold text-[#0F1419] hover:bg-[#E9EEF6] flex-shrink-0 whitespace-nowrap"
          >
            <span className="hidden md:inline">{scopeLabel}</span>
            <HiChevronDown
              className={`w-3.5 h-3.5 text-gray-500 transition-transform ${
                isSearchOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Divider */}
          <div className="w-px h-5 bg-[#E0E4EA] flex-shrink-0" />

          {/* Search area */}
          <div className="flex items-center gap-2.5 flex-1 min-w-0 px-3.5">
            <HiOutlineSearch className="w-[18px] h-[18px] text-gray-400 flex-shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                isCompact ? "Search..." : "Search a player, club, agent..."
              }
              className="w-full min-w-0 bg-transparent text-[13.5px] text-[#0F1419] placeholder:text-gray-400 border-0 focus:outline-none focus:ring-0 p-0"
            />
          </div>
        </div>

        {/* Scope dropdown */}
        {isSearchOpen && (
          <div className="absolute left-0 top-[44px] w-[200px] bg-white border border-gray-200 rounded-xl shadow-[0_12px_32px_-12px_rgba(15,20,25,0.25)] p-1.5 z-50">
            {searchTypes.map((type) => {
              const active = type.value === searchType;
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => {
                    setSearchType(type.value);
                    setIsSearchOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-[13.5px] font-medium hover:bg-[#F3F6FB] flex items-center justify-between ${
                    active ? "text-[#2283FF] bg-[#EEF4FE]" : "text-[#0F1419]"
                  }`}
                >
                  <span>{type.label}</span>
                  {active && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M5 12.5l4.5 4.5L19 7.5"
                        stroke="#2283FF"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </form>
    </div>
  );
};

export default SearchBar;
